import * as pdfjsLib from 'pdfjs-dist';

// Initialize worker
const workerContent = `
  importScripts('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js');
`;
const workerBlob = new Blob([workerContent], { type: 'text/javascript' });
const workerUrl = URL.createObjectURL(workerBlob);

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

export const parseBookingPDF = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';

    // Extract text from all pages
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      // Join with space to keep words separate, but might merge lines
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }

    return extractBookingInfo(fullText);
  } catch (error) {
    console.error('PDF parsing error:', error);
    throw new Error('Failed to parse PDF file');
  }
};

const normalizeDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    // Handle "Mon, Jan 1" format by adding current year if missing
    if (!/\d{4}/.test(dateStr)) {
      dateStr += ` ${new Date().getFullYear()}`;
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch (e) {
    return '';
  }
};

const formatPhoneNumber = (raw) => {
  // Remove all non-digits
  let digits = raw.replace(/\D/g, '');
  
  // Handle leading 1 for US numbers (e.g. 1770...)
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.substring(1);
  }

  // Ensure we have exactly 10 digits for a standard US format
  if (digits.length === 10) {
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  }
  
  // Return original if it doesn't match expected formats
  return raw;
};

const extractBookingInfo = (text) => {
  const info = {
    guestName: '',
    guestEmail: '',
    phone: '',
    reservationId: '',
    checkInDate: '',
    checkOutDate: '',
    doorCode: '',
  };

  // 1. Intelligent Date Extraction
  const findDateNearKeyword = (keywords) => {
    const textLower = text.toLowerCase();
    const datePattern = /((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,.]+(?:\d{1,2}(?:st|nd|rd|th)?)[\s,.]+(?:\d{4}))|(\d{1,2}\/\d{1,2}\/\d{2,4})|(\d{4}-\d{2}-\d{2})|(\d{1,2}[\s]+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s]+\d{4})/gi;

    for (const keyword of keywords) {
      const index = textLower.indexOf(keyword.toLowerCase());
      if (index !== -1) {
        const window = text.slice(index, index + 200);
        const match = window.match(datePattern);
        if (match) return normalizeDate(match[0]);
      }
    }
    return null;
  };

  info.checkInDate = findDateNearKeyword(['Check-in', 'Check in', 'Arrive', 'Arrival', 'Start Date', 'Dates:', 'Date:']);
  info.checkOutDate = findDateNearKeyword(['Check-out', 'Check out', 'Depart', 'Departure', 'End Date']);

  if (!info.checkInDate || !info.checkOutDate) {
    const allDatePattern = /((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,.]+\d{1,2}(?:st|nd|rd|th)?[\s,.]+\d{4})|(\d{1,2}\/\d{1,2}\/\d{2,4})/gi;
    const matches = text.match(allDatePattern);
    if (matches && matches.length >= 2) {
      const dates = matches
        .map(d => normalizeDate(d))
        .filter(d => d)
        .sort((a, b) => new Date(a) - new Date(b));
      if (dates.length >= 2) {
        info.checkInDate = dates[0];
        info.checkOutDate = dates[dates.length - 1];
      }
    }
  }

  // 2. Extract Guest Name (Fixed)
  // We capture a broad string first, then clean it up
  const nameRegex = /(?:Guest|Traveler|Booked by|Guest Name)(?:[\s:.-]*(?:name))?[\s:.-]+([A-Z][a-z.]*(?:\s+[A-Z][a-z.]*)+)/i;
  const nameMatch = text.match(nameRegex);
  
  if (nameMatch) {
    let rawName = nameMatch[1].trim();
    
    // Stop words that indicate the name field has ended and another field began
    // This fixes "Lydia M. Guest email" -> "Lydia M."
    const stopWords = [
      'Guest email', 'Guest phone', 'Email', 'Phone', 'Check-in', 'Check-out', 
      'Reservation', 'Date', 'Adults', 'Children', 'Listing', 'Hosted', 'Total'
    ];
    
    // Find the earliest occurrence of a stop word (case-insensitive)
    let cutOffIndex = rawName.length;
    for (const word of stopWords) {
      const idx = rawName.toLowerCase().indexOf(word.toLowerCase());
      if (idx !== -1 && idx < cutOffIndex) {
        cutOffIndex = idx;
      }
    }
    
    rawName = rawName.substring(0, cutOffIndex).trim();
    
    if (rawName.toLowerCase().startsWith('name ')) {
      rawName = rawName.substring(5).trim();
    }
    
    info.guestName = rawName;
  }

  // 3. Extract Emails
  const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
  const emails = text.match(emailRegex);
  if (emails && emails.length > 0) {
    const guestEmails = emails.filter(e => 
      !e.toLowerCase().includes('support') && 
      !e.toLowerCase().includes('noreply') && 
      !e.toLowerCase().includes('booking') && 
      !e.toLowerCase().includes('airbnb') && 
      !e.toLowerCase().includes('vrbo') &&
      !e.toLowerCase().includes('.png')
    );
    info.guestEmail = guestEmails.length > 0 ? guestEmails[0] : emails[0];
  }

  // 4. Extract Phone Numbers (Fixed)
  // Find sequences that look like phones, including those with country code 1
  const phoneRegex = /(?:\+?1[-.\s]?)?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
  const phones = text.match(phoneRegex);
  
  if (phones && phones.length > 0) {
    // Process the first match using the formatter which handles the '1' prefix
    info.phone = formatPhoneNumber(phones[0]);
  }

  // 5. Extract Reservation ID
  const patterns = [
    /HM[A-Z0-9]{8}/, 
    /HA-[A-Z0-9]{6}/, 
    /(?:Confirmation|Reservation|Booking)(?: Code| ID| #)?[:\s]+([A-Z0-9]{5,12})/i
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      info.reservationId = match[1] || match[0];
      break;
    }
  }

  return info;
};